import { CheckIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Group,
  List,
  Radio,
  Stack,
  Text,
  TextInput,
  useMantineColorScheme,
  useMantineTheme
} from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { AIAssistantModal } from '../../../../../components/common/AIAssistantModal';
import { CVSectionContainer } from '../../../../../components/common/CVSectionContainer';
import { DeleteConfirmationModal } from '../../../../../components/common/DeleteConfirmationModal';
import { FormattableTextArea } from '../../../../../components/common/FormattableTextArea';
import { ValidationResult } from '../../../../../components/common/ValidationResult';
import { BRAND_COLORS, FormUIColor } from '../../../../../constants/colorPalette';
import { useEditMode } from "../../../../../context/EditModeContext";
import { useTranslation } from '../../../../../hooks/useTranslation';
import { QualificationSummary } from '../../../../../types/cv';
import { ValidateableCvSections } from '../../../../../types/cvSections';

const getInstructions = (t: (key: string) => string) => (
  <Stack gap={5}>
    <Text fw={500}>{t('SUMMARY_INSTRUCTIONS_TITLE')}</Text>
    <Text size="sm">{t('SUMMARY_INSTRUCTIONS_MAIN')}</Text>
    <Text size="sm" mt={5}>{t('SUMMARY_INSTRUCTIONS_PARAGRAPHS')}</Text>
    <Text size="sm">{t('SUMMARY_INSTRUCTIONS_PARA1')}</Text>
    <Text size="sm">{t('SUMMARY_INSTRUCTIONS_PARA2')}</Text>
    <Text size="sm">{t('SUMMARY_INSTRUCTIONS_PARA3')}</Text>
    <Text size="sm" mt={5}>{t('SUMMARY_INSTRUCTIONS_VERSIONS')}</Text>
    <Text size="sm" mt={5} fw={500} c="blue">{t('SUMMARY_INSTRUCTIONS_NOTE')}</Text>
  </Stack>
);

interface QualificationSummariesProps {
  summaries: QualificationSummary[];
  onChange: (summaries: QualificationSummary[]) => void;
  personId?: number;
  cvId?: number;
}

export function QualificationSummaries({ summaries, onChange, personId, cvId }: QualificationSummariesProps) {
  const { isEditMode } = useEditMode();
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingSummaryId, setEditingSummaryId] = useState<number | null>(null);
  const [newSummary, setNewSummary] = useState<Partial<QualificationSummary>>({
    name: '',
    description: ''
  });
  const [editSummary, setEditSummary] = useState<QualificationSummary>({
    id: 0,
    personId: 0,
    name: '',
    description: '',
    selectedForCv: false
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [summaryToDelete, setSummaryToDelete] = useState<QualificationSummary | null>(null);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const [selectedSummaryId, setSelectedSummaryId] = useState<number | null>(null);
  const [showAddValidation, setShowAddValidation] = useState(false);
  const [showEditValidation, setShowEditValidation] = useState(false);

  // Function to format qualification summaries data for LLM validation
  const getQualificationSummariesSectionData = (): string => {
    if (!summaries || summaries.length === 0) {
      return 'No qualification summaries found.';
    }

    const selectedSummary = summaries.find(s => s.selectedForCv);

    let result = '';
    if (selectedSummary) {
      result += `Selected Summary for CV:\n`;
      result += `- Title: ${selectedSummary.name}\n`;
      result += `- Description: ${selectedSummary.description}\n\n`;
    } else {
      result += 'No summary selected for CV.\n\n';
    }

    result += `All Available Summaries (${summaries.length}):\n\n`;
    summaries.forEach((summary, index) => {
      result += `${index + 1}. ${summary.name} ${summary.selectedForCv ? '(Selected for CV)' : ''}\n`;
      result += `   ${summary.description}\n\n`;
    });

    return result.trim();
  };

  // Set initial selected summary based on summaries prop
  useEffect(() => {
    const selected = summaries.find(s => s.selectedForCv);
    console.log('Checking for selected summary:', selected?.id);
    if (selected) {
      setSelectedSummaryId(selected.id);
    } else {
      // If no summary is selected, ensure UI reflects that
      console.log('No selected summary found, clearing UI selection');
      setSelectedSummaryId(null);
    }
  }, [summaries]);

  // Close and reset all forms when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      setIsAddFormOpen(false);
      setEditingSummaryId(null);
      setNewSummary({
        name: '',
        description: ''
      });
      setDeleteModalOpen(false);
      setIsAiAssistantOpen(false);
      setShowAddValidation(false);
      setShowEditValidation(false);
    }
  }, [isEditMode]);

  const handleAddSummary = () => {
    setShowAddValidation(true);
    if (!newSummary.name || !newSummary.description) return;

    const summaryEntry: QualificationSummary = {
      id: 0, // Will be set by the backend
      personId: 0, // Will be set by the backend
      name: newSummary.name,
      description: newSummary.description,
      selectedForCv: false // Default to not selected
    };

    onChange([...summaries, summaryEntry]);
    setNewSummary({
      name: '',
      description: ''
    });
    setIsAddFormOpen(false);
    setShowAddValidation(false);
  };

  const handleRemoveSummary = (id: number) => {
    onChange(summaries.filter(summary => summary.id !== id));

    // If selected summary was deleted, reset selection
    if (selectedSummaryId === id) {
      setSelectedSummaryId(null);
    }
  };

  const handleSelectSummary = (id: number) => {
    console.log('Selecting summary:', id);
    setSelectedSummaryId(id);

    // Update all summaries - select the chosen one, deselect all others
    onChange(summaries.map(summary =>
      summary.id === id
        ? { ...summary, selectedForCv: true }
        : { ...summary, selectedForCv: false }
    ));
  };

  const handleEditSummary = (summary: QualificationSummary) => {
    setEditingSummaryId(summary.id);
    setEditSummary(summary);
  };

  const saveEditSummary = () => {
    setShowEditValidation(true);
    if (!editingSummaryId || !editSummary.name || !editSummary.description) return;

    // If the edited summary is being set as selected, we need to unselect all others
    if (editSummary.selectedForCv) {
      onChange(summaries.map(summary =>
        summary.id === editingSummaryId ? editSummary : { ...summary, selectedForCv: false }
      ));
      setSelectedSummaryId(editingSummaryId);
    } else {
      // Not changing selection status, just update this summary
      onChange(summaries.map(summary =>
        summary.id === editingSummaryId ? editSummary : summary
      ));
    }

    setEditingSummaryId(null);
    setShowEditValidation(false);
  };

  const openDeleteConfirmation = (summary: QualificationSummary) => {
    setSummaryToDelete(summary);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (summaryToDelete) {
      handleRemoveSummary(summaryToDelete.id);
      setDeleteModalOpen(false);
    }
  };

  const handleAiAssistant = () => {
    if (!personId) return;
    setIsAiAssistantOpen(true);
  };

  const handleApplyAIChanges = (text: string, profileText?: string) => {
    setNewSummary(prev => ({
      ...prev,
      description: text,
      ...(profileText !== undefined && { name: profileText }),
    }));
  };

  return (
    <CVSectionContainer
      sectionId="summary-qualifications"
      title={t('CVDETAIL_SUMMARY')}
      buttonText={isEditMode ? t('CVEDITOR_ADD_ITEM', { item: t('SUMMARY_ITEM_NAME') }) : undefined}
      onAddClick={isEditMode ? () => setIsAddFormOpen(true) : undefined}
      instructions={isEditMode ? getInstructions(t) : undefined}
      faqPath="professional-summary"
      personId={personId}
      cvId={cvId}
      getSectionData={getQualificationSummariesSectionData}
    >
      {isEditMode && summaries.length > 0 && (
        <Text size="sm" mb="md" c="dimmed">
          {t('SUMMARY_SELECT_INSTRUCTION')}
        </Text>
      )}

      <ValidationResult section={ValidateableCvSections.Summary} />

      <Collapse in={isAddFormOpen}>
        <Box mb="md" p="md" style={{ border: `2px solid ${theme.colors.cyan[7]}`, borderRadius: theme.radius.sm, backgroundColor: isDark ? theme.colors.dark[6] : FormUIColor.ADD_FORM_BG }}>
          <Stack gap="md">
            <Button
              variant="outline"
              size="xs"
              color="brand"
              onClick={handleAiAssistant}
              style={{ alignSelf: 'flex-start', whiteSpace: 'nowrap', background: BRAND_COLORS.backgroundLightPurple }}
            >
              Generate Summary
            </Button>
            <TextInput
              label={t('SUMMARY_NAME_LABEL')}
              placeholder={t('SUMMARY_NAME_PLACEHOLDER')}
              value={newSummary.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSummary(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            <Box style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'flex-end' }}>
              <FormattableTextArea
                placeholder={t('SUMMARY_DESCRIPTION_PLACEHOLDER')}
                minRows={5}
                maxRows={20}
                value={newSummary.description || ''}
                onChange={(value) => setNewSummary(prev => ({ ...prev, description: value }))}
                required
                style={{ flex: 1, minWidth: 0 }}
                label={t('SUMMARY_DESCRIPTION_LABEL')}
                enableImprovement={true}
                improvePrompt="Please improve this summary while maintaining its core facts and details:"
              />
            </Box>

            <Group justify="flex-end" gap="sm">
              {showAddValidation && (() => {
                const errors: string[] = [];
                if (!newSummary.name) errors.push('Title is required');
                if (!newSummary.description) errors.push('Description is required');
                return errors.length > 0 ? (
                  <Alert color="red" w="100%" py="xs">
                    <List size="sm" spacing={2}>
                      {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                    </List>
                  </Alert>
                ) : null;
              })()}
              <Button
                variant="outline"
                leftSection={<XMarkIcon width={16} height={16} />}
                onClick={() => { setIsAddFormOpen(false); setShowAddValidation(false); }}
              >
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                leftSection={<CheckIcon width={16} height={16} />}
                onClick={handleAddSummary}
              >
                Add Summary
              </Button>
            </Group>
          </Stack>
        </Box>
      </Collapse>

      {summaries.length > 0 ? (
        <Radio.Group
          value={selectedSummaryId?.toString() || ""}
          onChange={(value) => handleSelectSummary(parseInt(value))}
        >
          <Stack gap="md">
            {summaries.map((summary) => (
              <Box key={summary.id} mb="md">
                {editingSummaryId === summary.id ? (
                  <Box p="md" style={{ border: `2px solid ${theme.colors.blue[5]}`, borderRadius: theme.radius.sm, backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.blue[0] }}>
                    <Stack gap="md">
                      <TextInput
                        label={t('SUMMARY_NAME_LABEL')}
                        placeholder={t('SUMMARY_NAME_PLACEHOLDER')}
                        value={editSummary.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditSummary(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />

                      <FormattableTextArea
                        label={t('SUMMARY_DESCRIPTION_LABEL')}
                        placeholder={t('SUMMARY_DESCRIPTION_PLACEHOLDER')}
                        minRows={5}
                        maxRows={20}
                        value={editSummary.description}
                        onChange={(value) => setEditSummary(prev => ({ ...prev, description: value }))}
                        required
                        enableImprovement={true}
                        improvePrompt="Please improve this summary while maintaining its core facts and details:"
                      />

                      <Radio
                        label={t('SUMMARY_USE_FOR_CV')}
                        checked={editSummary.selectedForCv}
                        onChange={() => setEditSummary(prev => ({ ...prev, selectedForCv: !prev.selectedForCv }))}
                      />

                      <Group justify="flex-end" gap="sm">
                        {showEditValidation && (() => {
                          const errors: string[] = [];
                          if (!editSummary.name) errors.push('Title is required');
                          if (!editSummary.description) errors.push('Description is required');
                          return errors.length > 0 ? (
                            <Alert color="red" w="100%" py="xs">
                              <List size="sm" spacing={2}>
                                {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                              </List>
                            </Alert>
                          ) : null;
                        })()}
                        <Button
                          variant="outline"
                          leftSection={<XMarkIcon width={16} height={16} />}
                          onClick={() => { setEditingSummaryId(null); setShowEditValidation(false); }}
                        >
                          {t('COMMON_CANCEL')}
                        </Button>
                        <Button
                          leftSection={<CheckIcon width={16} height={16} />}
                          onClick={saveEditSummary}
                        >
                          {t('COMMON_SAVE')}
                        </Button>
                      </Group>
                    </Stack>
                  </Box>
                ) : (
                  <Card
                    padding="md"
                    withBorder
                    style={{ borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[3] }}
                  >
                    <Group justify="space-between" align="flex-start">
                      <Stack gap="xs">
                        <Text fw={500}>{summary.name}</Text>
                      </Stack>
                      {isEditMode && (
                        <Group gap={8}>
                          <Radio
                            value={summary.id.toString()}
                            label={t('SUMMARY_USE_IN_CV')}
                          />
                          <ActionIcon variant="subtle" color="gray.7" onClick={() => handleEditSummary(summary)}>
                            <PencilIcon style={{ width: 16, height: 16 }} />
                          </ActionIcon>
                          <ActionIcon variant="subtle" color="red" onClick={() => openDeleteConfirmation(summary)}>
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </ActionIcon>
                        </Group>
                      )}
                    </Group>
                    <Text size="sm" mt="sm" style={{ whiteSpace: 'pre-wrap' }}>{summary.description}</Text>
                  </Card>
                )}
              </Box>
            ))}
          </Stack>
        </Radio.Group>
      ) : (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          {t('SUMMARY_NO_ITEMS')}
        </Text>
      )}



      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={summaryToDelete?.name}
      />

      <AIAssistantModal
        opened={isAiAssistantOpen}
        onClose={() => setIsAiAssistantOpen(false)}
        onApply={handleApplyAIChanges}
        initialText={newSummary.description}
        initialProfile={newSummary.name ?? ''}
        personId={personId}
      />
    </CVSectionContainer>
  );
}

